import { forwardRef } from "react";

import { Box, IconButton } from "@mui/material";
import { DatePicker as MUIDatePicker } from "@mui/x-date-pickers/DatePicker";
import { gray } from "configs/colors";
import { shadows } from "configs/shadows";
import { rem } from "utils";

import { useDatePicker } from "./DatePicker.hooks";
import {
  DatePickerProps,
  DatePickerReference,
  DatePickerTextFieldProps,
} from "./DatePicker.types";
import { CalendarIcon } from "../Icon";
import TextField from "../TextField/TextField";

const DatePickerTextField = ({
  InputProps,
  onClick,
  onChange,
  ...innerProps
}: DatePickerTextFieldProps) => {
  return (
    <TextField
      {...innerProps}
      onChangeEvent={onChange}
      onClick={onClick}
      InputProps={{
        ...(InputProps ?? {}),
        startAdornment: (
          <IconButton
            disabled={innerProps.disabled}
            sx={{ marginX: rem(-10) }}
            onClick={
              onClick as unknown as React.MouseEventHandler<HTMLButtonElement>
            }
          >
            <CalendarIcon fontSize="small" sx={{ color: gray[700] }} />
          </IconButton>
        ),
        endAdornment: null, // Hide default "Calendar" icon of MUI Date Picker
        readOnly: true,
      }}
    />
  );
};

const DatePicker = forwardRef<DatePickerReference, DatePickerProps>(
  (
    {
      defaultValue,
      value,
      placeholder,
      required,
      helperText,
      error,
      onChange,
      ...innerProps
    },
    ref
  ) => {
    const {
      rootRef,
      valueState,
      openCalendar,
      onChangeHandler,
      onCalendarCloseHandler,
      toggleCalendar,
    } = useDatePicker({
      ref,
      defaultValue,
      value,
      onChange,
    });

    return (
      <Box ref={rootRef}>
        <MUIDatePicker
          value={valueState}
          open={openCalendar}
          onChange={onChangeHandler}
          onClose={onCalendarCloseHandler}
          slots={{ textField: DatePickerTextField }}
          slotProps={{
            popper: {
              sx: {
                ".MuiPickersPopper-paper": {
                  boxShadow: shadows.lg,
                },
              },
            },
            textField: {
              required,
              placeholder,
              helperText,
              error,
              onClick: toggleCalendar as unknown as never,
            },
          }}
          {...innerProps}
        />
      </Box>
    );
  }
);

export default DatePicker;
